import { computed, onBeforeUnmount, reactive, watch } from "@vue/composition-api";
import debounce from "lodash/debounce";

import useAxios from "@/hooks/useAxios";
import useProxy from "@/hooks/useProxy";
import {
  getDomain,
  getScoreLevel,
  prettifyOrigin,
} from "./source-presentation";

const DEFAULT_SCORE_MIN = 8;

/**
 * Flatten one `ScoredNewsData` row into the shape the views consume.
 *
 * `item.scores` is deliberately NOT spread: NewsRadarDataScoresRead carries its
 * own `id`, `title`, `description` and `publication_date`, which would shadow
 * the ones from `news_data`. Only `notes` (the LLM's score rationale) is kept.
 */
const normalizeItem = (item, configByEntry, isVideo = false) => {
  const data = item.news_data || {};
  const config = configByEntry[data.from_entry];

  return {
    id: data.id,
    title: data.title || "—",
    url: data.url || "",
    description: data.description || "",
    preview: data.preview || "",
    publicationDate: data.publication_date || "",
    sourceName: data.news_radar_source?.name || "",
    fromEntry: data.from_entry || "",
    originLabel: config?.name || prettifyOrigin(data.from_entry),
    // Vide en vidéo : le domaine d'une vidéo est sa plateforme (youtube.com sur
    // toutes les lignes), il ne dit rien de la source. Ce qui compte, c'est la
    // chaîne, et elle est déjà là : c'est l'origine. Les vues suivent la même
    // règle que pour le score — un champ vide masque la colonne, la mention du
    // panneau de détail et bascule la carte sur l'origine.
    domain: isVideo ? "" : getDomain(data.url),
    // `null` en vidéo : une source vidéo est hors scoring (la description RSS
    // d'une vidéo est pauvre ou vide, et on ne peut pas scorer sa parole sans
    // la transcrire). Les vues masquent la pastille plutôt que d'afficher 0.
    score: item.score ?? null,
    level: item.score === null || item.score === undefined
      ? null
      : getScoreLevel(item.score),
    isVideo,
    used: item.used ?? false,
    notes: item.scores?.notes || "",
  };
};

/**
 * Fetches and paginates the scored source list.
 * Sole consumer of the global `common/applyFilters` flag for this page.
 */
export function useGlobalWatchResults({ onNewSearch }) {
  const { axiosGet } = useAxios();
  const { store } = useProxy();

  const page = reactive({
    loading: false,
    forceCall: false,
    pendingReload: false,
    hasSearched: false,
    search: "",
    sortBy: "date",
    view: "table",
    hideTreated: true,
    items: [],
  });

  const pagination = reactive({
    total: 0,
    numberOfPages: 0,
    options: {
      page: 1,
      itemsPerPage: 20,
    },
  });

  let syncedPage = pagination.options.page;
  let syncedItemsPerPage = pagination.options.itemsPerPage;

  const syncOptionsState = () => {
    syncedPage = pagination.options.page;
    syncedItemsPerPage = pagination.options.itemsPerPage;
  };

  // =>  COMPUTED
  const applyFilters = computed(() => store.getters["common/getApplyFilters"]);
  const siteId = computed(() => store.getters["aiFactory/getSelectedSites"]);
  const mediaType = computed(
    () => store.getters["newsRadar/getMediaType"] || "written"
  );
  const isVideo = computed(() => mediaType.value === "video");
  const scoreMin = computed(() => store.getters["aiFactory/getScoreMin"]);
  const sources = computed(() => store.getters["newsRadar/getSources"]);
  const configs = computed(() => store.getters["newsRadar/getConfigs"]);
  const origins = computed(() => store.getters["newsRadar/getOrigins"]);
  const dates = computed(() => store.getters["newsRadar/getDates"] || []);

  const configByEntry = computed(() => {
    const configs = store.getters["newsRadar/getConfigList"] || [];
    return configs.reduce((acc, config) => {
      if (config.entry) acc[config.entry] = config;
      return acc;
    }, {});
  });

  // "Masquer les déjà traités" est filtré côté backend (exclude_used) pour
  // garantir une page complète de 20 résultats ; le filtre local est conservé
  // en garde défensive (no-op si le backend a déjà filtré).
  const visibleItems = computed(() =>
    page.hideTreated ? page.items.filter((item) => !item.used) : page.items
  );

  const shownCount = computed(() => visibleItems.value.length);

  // =>  METHODS
  const fetchResults = async () => {
    if (!applyFilters.value && !page.forceCall) {
      return;
    }

    const numericSiteId = Number(siteId.value);
    if (!Number.isInteger(numericSiteId) || numericSiteId <= 0) {
      page.forceCall = false;
      store.dispatch("common/updateApplyFilters", false);
      return;
    }

    if (page.loading) {
      page.forceCall = false;
      page.pendingReload = true;
      return;
    }

    page.loading = true;
    page.forceCall = false;
    let requestSucceeded = false;

    try {
      const {
        data: { total, items },
      } = await axiosGet("/news-radar-data/scored_data", {
        site_id: numericSiteId,
        score_min: Number(scoreMin.value) || DEFAULT_SCORE_MIN,
        // `hideTreated` est actif par défaut côté UI (exclude_used: true).
        // En cas de test de parité avec /newsradar-db (qui n'exclut pas les
        // déjà traités), désactiver cette option ; sinon l'écart est attendu.
        exclude_used: Boolean(page.hideTreated),
        ...(sources.value?.length
          ? { source_ids: sources.value.join(",") }
          : {}),
        ...(configs.value?.length
          ? { config_ids: configs.value.join(",") }
          : {}),
        ...(origins.value?.length ? { origins: origins.value.join(",") } : {}),
        ...(page.search.trim() ? { search: page.search.trim() } : {}),
        date_start: dates.value[0],
        date_end: dates.value[1],
        page: pagination.options.page,
        limit: pagination.options.itemsPerPage,
        media_type: mediaType.value,
        // Only "score" is handled server-side; anything else sorts by date desc.
        // Ignoré en vidéo : une source vidéo n'a pas de score, le serveur trie
        // par date de publication quoi qu'on lui demande.
        ...(page.sortBy === "score" && !isVideo.value
          ? { sort_by: "score", sort_desc: true }
          : {}),
      });

      page.items = (items || []).map((item) =>
        normalizeItem(item, configByEntry.value, isVideo.value)
      );
      pagination.total = total;
      pagination.numberOfPages = Math.ceil(
        total / pagination.options.itemsPerPage
      );
      page.hasSearched = true;
      requestSucceeded = true;
    } catch (error) {
      console.error("error while fetching scored data", error);
    }

    page.loading = false;

    // Released only once the request is done, so other components listening on
    // the flag can't fire a second round.
    store.dispatch("common/updateApplyFilters", false);

    if (page.pendingReload) {
      page.pendingReload = false;
      reload();
      return;
    }

    if (requestSucceeded) {
      if (pagination.total === 0 && pagination.options.page !== 1) {
        // Evite un second appel API inutile quand total === 0 : la réponse
        // courante est déjà vide.
        pagination.options.page = 1;
        syncOptionsState();
      } else if (
        pagination.total > 0 &&
        pagination.options.page > pagination.numberOfPages
      ) {
        pagination.options.page = pagination.numberOfPages;
        syncOptionsState();
        reload();
      }
    }
  };

  const reloadNow = () => {
    page.forceCall = true;
    fetchResults();
  };

  const resetToFirstPage = () => {
    if (pagination.options.page !== 1) {
      pagination.options.page = 1;
      syncOptionsState();
    }
    reloadNow();
  };

  const debouncedReload = debounce(resetToFirstPage, 500);

  const reload = () => {
    debouncedReload.cancel();
    reloadNow();
  };

  const resetToFirstPageOrReload = (debounced = false) => {
    if (!page.hasSearched && !page.loading) {
      return;
    }
    debouncedReload.cancel();
    if (debounced) {
      debouncedReload();
      return;
    }
    resetToFirstPage();
  };

  onBeforeUnmount(() => {
    debouncedReload.cancel();
  });

  // =>  WATCH
  watch(applyFilters, () => {
    if (applyFilters.value) {
      debouncedReload.cancel();
      page.pendingReload = false;
      pagination.options.page = 1;
      syncOptionsState();
      page.forceCall = true;
      if (typeof onNewSearch === "function") onNewSearch();
      fetchResults();
    }
  });

  watch(
    () => page.search,
    () => resetToFirstPageOrReload(true)
  );

  watch(
    () => page.sortBy,
    () => resetToFirstPageOrReload()
  );

  watch(
    () => page.hideTreated,
    () => resetToFirstPageOrReload()
  );

  watch(
    () => [pagination.options.page, pagination.options.itemsPerPage],
    ([nextPage, nextItemsPerPage]) => {
      if (
        nextPage === syncedPage &&
        nextItemsPerPage === syncedItemsPerPage
      ) {
        return;
      }
      if (nextItemsPerPage !== syncedItemsPerPage && nextPage !== 1) {
        pagination.options.page = 1;
      }
      syncOptionsState();
      if (page.hasSearched || page.loading) {
        reload();
      }
    }
  );

  // =>  DATA
  return {
    fetchResults,
    isVideo,
    page,
    pagination,
    reload,
    shownCount,
    visibleItems,
  };
}
